import debounce from 'lodash.debounce'

export function isEqualNumber(val1, val2) {
  return Number(val1) === Number(val2)
}

/**
 * Additional functionality for informing developers
 * about deprecated events or methods
 *
 * @param target
 * @param deprecated
 */
export function deprecateNotification(target, deprecated) {
  deprecated &&
    console.warn(
      `DEPRECATED: "${target}" is deprecated and will be removed | ${deprecated}`,
    )
}

/**
 * Opens a URL in a new window/tab once per unique event.
 * Uses localStorage to lock events across tabs, and removes expired locks on each call.
 *
 * @param {string} id - Unique event identifier
 * @param {string} url - URL to open
 * @param {string} [windowName='_blank'] - Target window name
 * @param {number} [ttl=10000] - Lock time-to-live in ms (default: 10s)
 */
export function openOncePerEvent(id, url, windowName = '_blank', ttl = 10000) {
  const now = Date.now()
  const prefix = 'event-lock-'
  const lockKey = `${prefix}${id}`

  // Clean up expired event-lock keys
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i)
    if (key?.startsWith(prefix)) {
      const expiresAt = parseInt(localStorage.getItem(key) || '0', 10)
      if (!expiresAt || expiresAt <= now) {
        localStorage.removeItem(key)
      }
    }
  }

  const existing = localStorage.getItem(lockKey)
  if (existing && parseInt(existing, 10) > now) return

  localStorage.setItem(lockKey, (now + ttl).toString())
  window.open(url, windowName)
}

/**
 * Run the first call immediately and debounce all subsequent calls.
 * Unlike leading debounce, idle periods do not reset the first-call behavior.
 *
 * @param {Function} callback - Function to invoke
 * @param {number} wait - Debounce delay in milliseconds
 * @returns {Function} Wrapped function with cancel and flush methods
 */
export function debounceAfterFirst(callback, wait) {
  const debounced = debounce(callback, wait)
  let isFirstCall = true

  function wrapped(...args) {
    if (!isFirstCall) return debounced.apply(this, args)

    isFirstCall = false
    return callback.apply(this, args)
  }

  wrapped.cancel = debounced.cancel
  wrapped.flush = debounced.flush
  return wrapped
}
